import { TaInput } from '@components/shared';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { PasswordRequirements } from '@features/auth/login';
import { SettingsCard } from '@features/dashboard/components/SettingsCard/SettingsCard';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { changePasswordService } from 'services/auth/authService';
import * as Yup from 'yup';

type PasswordFieldProps = {
	id: string;
	label: string;
	value: string;
	error?: string;
	touched?: boolean;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const PasswordCard = () => {
	const { t } = useTranslation();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	const validationSchema = Yup.object({
		oldPassword: Yup.string().min(8, t('passwordMinLength')).required(t('oldPasswordRequired')),
		newPassword: Yup.string().min(8, t('passwordMinLength')).required(t('newPasswordRequired')),
		repeatPassword: Yup.string()
			.oneOf([Yup.ref('newPassword')], t('passwordsNotMatching'))
			.min(8, t('passwordMinLength'))
			.required(t('confirmPasswordRequired')),
	});

	const formik = useFormik({
		initialValues: { oldPassword: '', newPassword: '', repeatPassword: '' },
		validationSchema,
		validateOnBlur: true,
		validateOnChange: true,
		onSubmit: async () => {
			try {
				const { oldPassword, newPassword } = formik.values;
				await changePasswordService({ oldPassword, newPassword });

				showSnackbar(t('success'), t('passwordSuccessfullyChanged'), 'success');
				formik.resetForm();
			} catch (error) {
				showSnackbar(t('error'), t(error.response?.data?.error || 'somethingWentWrong'), 'error');
			}
		},
	});

	const renderError = useMemo(() => {
		const errorText = formik.errors.repeatPassword || formik.errors.newPassword;
		return (formik.touched.newPassword && formik.errors.newPassword) ||
			(formik.touched.repeatPassword && formik.errors.repeatPassword) ? (
			<FormHelperText
				sx={{
					display: 'flex',
					marginTop: '10px',
					gap: '9px',
					marginBottom: '4px',
					color: '#FD3333',
					fontSize: '16px',
				}}
			>
				<ErrorOutlineOutlinedIcon />
				{t(errorText || '')}
			</FormHelperText>
		) : null;
	}, [formik.errors, formik.touched, t]);

	const renderPasswordField = ({
		id,
		label,
		value,
		error,
		touched,
		onChange,
	}: PasswordFieldProps) => (
		<div>
			<TaInput
				icon={<LockOutlinedIcon sx={{ color: touched && error ? '#FD3333' : '#7B809A' }} />}
				id={id}
				label={t(label)}
				size="small"
				type="password"
				value={value}
				onChange={onChange}
				onBlur={formik.handleBlur}
				error={!!error}
				touched={touched}
			/>
			{touched && <PasswordRequirements password={value} />}
		</div>
	);

	return (
		<form className="w-full" onSubmit={formik.handleSubmit}>
			<SettingsCard
				title={t('setPassword')}
				subTitle={t('setPasswordNextTime')}
				buttonBgColor="#00AB89"
				buttonText={t('updatePassword')}
				onClick={formik.submitForm}
			>
				{renderPasswordField({
					id: 'oldPassword',
					label: 'passwordOld',
					value: formik.values.oldPassword,
					error: formik.errors.oldPassword,
					touched: formik.touched.oldPassword,
					onChange: formik.handleChange,
				})}
				{renderPasswordField({
					id: 'newPassword',
					label: 'passwordNew',
					value: formik.values.newPassword,
					error: formik.errors.newPassword,
					touched: formik.touched.newPassword,
					onChange: formik.handleChange,
				})}
				{renderPasswordField({
					id: 'repeatPassword',
					label: 'repeatNewPassword',
					value: formik.values.repeatPassword,
					error: formik.errors.repeatPassword,
					touched: formik.touched.repeatPassword,
					onChange: formik.handleChange,
				})}
				{renderError}
			</SettingsCard>
		</form>
	);
};
